import { gql, useMutation } from '@apollo/client';
import {
  Button,
  Divider,
  HStack,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  useToast,
} from '@chakra-ui/react';
import * as React from 'react';

import { OrganizationCsvUploadOperator } from '@/graphql-types/globalTypes';
import { useMemberManagement } from '@/hooks/useMemberManagement';
import { useUploadFileToS3 } from '@/hooks/useUploadFileToS3';
import { logError } from '@/imports/logging/ClientLogger';
import { SuccessModal } from '@/imports/ui/admin/components/SuccessModal';
import { ImportTypeOption } from '@/imports/ui/components/csv/CsvImportConfiguration';
import { CsvImportModalFrameImportContent } from '@/imports/ui/components/csv/CsvImportModalFrameImportContent';
import { CsvImportTemplate } from '@/imports/ui/components/csv/CsvImportTemplate';
import { useCsvImportForeignKeyConfig } from '@/imports/ui/components/csv/useCsvImportConfig';

import {
  CompaniesCsvImportModal_ExecuteCompanyCsvUpload,
  CompaniesCsvImportModal_ExecuteCompanyCsvUploadVariables,
} from './graphql-types/CompaniesCsvImportModal_ExecuteCompanyCsvUpload';

import { FieldValidationError } from '../../../../../../client/legacy-shared-modules/csv/csv-field-mapping';
import {
  ValidationFn,
  getValidateCsvFileHeaders,
  normalizeHeader,
} from '../../../../../../client/legacy-shared-modules/csv/helpers';
import { usePrepareCompanyCsv } from './hooks/usePrepareCompanyCsv';
import {
  FOREIGN_KEY_OPTIONS,
  REQUIRED_COMPANY_HEADERS_FOR_INSERT,
  getCompanyUploadTemplate,
} from './utils';

const EXECUTE_COMPANY_CSV_UPLOAD = gql`
  mutation CompaniesCsvImportModal_ExecuteCompanyCsvUpload(
    $input: ExecuteOrganizationCsvUploadInput!
  ) {
    executeOrganizationCsvUpload(input: $input) {
      csvUploadId
    }
  }
`;

type ICompaniesCsvImportModalProps = {
  open: boolean;
  onClose: () => void;
};

export const CompaniesCsvImportModal: React.FC<
  ICompaniesCsvImportModalProps
> = (props) => {
  const [uploadCsvData, { loading: uploadMutationLoading }] = useMutation<
    CompaniesCsvImportModal_ExecuteCompanyCsvUpload,
    CompaniesCsvImportModal_ExecuteCompanyCsvUploadVariables
  >(EXECUTE_COMPANY_CSV_UPLOAD);

  const [csvFile, setCsvFile] = React.useState<File | null>(null);
  const [validationErrors, setValidationErrors] =
    React.useState<FieldValidationError | null>(null);
  const [showSuccessModal, setShowSuccessModal] =
    React.useState<boolean>(false);
  const [uploadedCsv, setUploadedCsv] = React.useState<boolean>(false);
  const toast = useToast();

  const { isActive: memberManagementEnabled } = useMemberManagement();

  const {
    prepareCompanyCsv,
    prepareCompanyCsvData,
    prepareCompanyCsvLoading,
    clearCsvResult,
  } = usePrepareCompanyCsv();

  const [uploadFileToS3, { isUploading, error: uploadError, isCanceled }] =
    useUploadFileToS3();

  const prepOrganizationCsvUpload =
    prepareCompanyCsvData?.prepOrganizationCsvUpload;

  const handleUploadFileToS3 = React.useCallback(
    async (file: File): Promise<void> => {
      try {
        if (
          prepOrganizationCsvUpload?.metadataHeaders &&
          prepOrganizationCsvUpload?.presignedS3Url
        ) {
          const newMetadataHeaders =
            prepOrganizationCsvUpload.metadataHeaders.reduce(
              (obj: Record<string, string>, { name, value }) => (
                (obj[name] = value), obj
              ),
              {}
            );

          await uploadFileToS3({
            file,
            presignedS3Url: prepOrganizationCsvUpload.presignedS3Url,
            metadataHeaders: newMetadataHeaders,
          });
        } else {
          // eslint-disable-next-line quotes
          logError(new Error("Can't upload csv file with failed prep"));
        }
      } catch (err) {
        toast({
          title: 'Failed to upload csv. Please try again.',
          status: 'error',
        });
        logError(err);
      }
    },
    [toast, uploadFileToS3, prepOrganizationCsvUpload]
  );

  React.useEffect(() => {
    if (prepareCompanyCsvData && !uploadedCsv && !!csvFile) {
      setUploadedCsv(true);
      handleUploadFileToS3(csvFile).catch((err) => logError(err));
    }
  }, [
    prepareCompanyCsvData,
    setUploadedCsv,
    handleUploadFileToS3,
    csvFile,
    uploadedCsv,
  ]);

  const handleExecuteCompanyCsvUpload = async (
    csvUploadId: string,
    operator: string,
    matchField: string
  ): Promise<void> => {
    try {
      await uploadCsvData({
        variables: {
          input: {
            csvUploadId,
            operator: operator as OrganizationCsvUploadOperator,
            matchField,
          },
        },
      });
      setShowSuccessModal(true);
    } catch (err) {
      toast({
        title: 'Failed to execute organization csv upload. Please try again.',
        status: 'error',
      });
      logError(err);
    }
  };

  const getValidateFileFn = (
    importType: string,
    foreignKey: string
  ): ((file: File) => Promise<void>) => {
    const validateHeaders: ValidationFn = (headers) => {
      const matchingLabel = FOREIGN_KEY_OPTIONS.filter(
        (option) => option.value === foreignKey
      )[0].label;

      if (importType === OrganizationCsvUploadOperator.INSERT) {
        const errors = REQUIRED_COMPANY_HEADERS_FOR_INSERT.filter(
          (requiredHeader) => !headers.includes(normalizeHeader(requiredHeader))
        ).map((missingField) => `Missing required field: ${missingField}`);
        if (!headers.includes(normalizeHeader(matchingLabel))) {
          errors.push(`Missing match field: ${matchingLabel}`);
        }
        if (errors.length > 0) {
          return { errors };
        }
      } else {
        if (!headers.includes(normalizeHeader(matchingLabel))) {
          return { errors: [`Missing match field: ${matchingLabel}`] };
        }
      }

      return null;
    };
    return async (file) => {
      await getValidateCsvFileHeaders({
        validateHeaders,
        onSuccess: (successFile) => {
          setValidationErrors(null);
          setCsvFile(successFile);
        },
        onErrors: (errors) => {
          setCsvFile(null);
          setValidationErrors(errors);
        },
      })(file);
    };
  };

  const onConfigChange = React.useCallback(
    (importType, foreignKey) => {
      if (!csvFile) {
        return;
      }
      void getValidateFileFn(importType, foreignKey)(csvFile);
    },
    [csvFile]
  );

  const { importType, onImportTypeChange, foreignKeyConfig } =
    useCsvImportForeignKeyConfig({
      defaultForeignKey: '_id',
      defaultImportType: OrganizationCsvUploadOperator.INSERT,
      onConfigChange,
    });

  const onClose = (): void => {
    setValidationErrors(null);
    setCsvFile(null);
    onImportTypeChange(OrganizationCsvUploadOperator.INSERT);
    setUploadedCsv(false);
    clearCsvResult();
    props.onClose();
  };

  const handleRemoveFile = (e: React.SyntheticEvent): void => {
    e.stopPropagation();
    setValidationErrors(null);
    setCsvFile(null);
    setUploadedCsv(false);
    clearCsvResult();
  };

  const submitFileUpload = async (csvUploadId: string): Promise<void> => {
    await handleExecuteCompanyCsvUpload(
      csvUploadId,
      importType,
      foreignKeyConfig.foreignKey
    );
    onClose();
  };

  const onFileChange = async (file: File | null): Promise<void> => {
    clearCsvResult();
    await prepareCompanyCsv();
    if (!!file) {
      const validateFunc = getValidateFileFn(
        importType,
        foreignKeyConfig.foreignKey
      );
      await validateFunc(file);
    }
  };

  const importTypeOptions: ImportTypeOption[] = [
    { value: OrganizationCsvUploadOperator.INSERT, label: 'Insert' },
    { value: OrganizationCsvUploadOperator.UPDATE, label: 'Update' },
  ];

  const hasLoadingSideEffect =
    isUploading || prepareCompanyCsvLoading || uploadMutationLoading;

  const isDisabled =
    isCanceled ||
    !!uploadError ||
    hasLoadingSideEffect ||
    !!(validationErrors && validationErrors.errors) ||
    !csvFile;

  if (showSuccessModal) {
    return (
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
      />
    );
  }

  return (
    <Modal isOpen={props.open} onClose={onClose} size="xl">
      <ModalOverlay />

      <ModalContent>
        <ModalHeader as="h3" py="4" px="6">
          {`Bulk Import Organization`}
        </ModalHeader>

        <Divider />

        <ModalCloseButton />

        <ModalBody p="6">
          <CsvImportModalFrameImportContent
            validationErrors={validationErrors}
            importConfig={{
              importType,
              importTypeOptions,
              onImportTypeChange,
            }}
            file={csvFile}
            onFileChange={onFileChange}
            onRemoveFile={handleRemoveFile}
            hasLoadingSideEffect={hasLoadingSideEffect}
          />
        </ModalBody>

        <Divider />

        <HStack justify="space-between" m="6">
          <Link
            {...getCompanyUploadTemplate(memberManagementEnabled)}
            as={CsvImportTemplate}
          >
            Download CSV Template
          </Link>

          <Button
            isLoading={hasLoadingSideEffect}
            isDisabled={isDisabled}
            onClick={async () => {
              if (
                prepareCompanyCsvData?.prepOrganizationCsvUpload.csvUploadId
              ) {
                await submitFileUpload(
                  prepareCompanyCsvData.prepOrganizationCsvUpload.csvUploadId
                );
              } else {
                logError(new Error('missing csvUploadId'));
              }
            }}
          >
            Start Import
          </Button>
        </HStack>
      </ModalContent>
    </Modal>
  );
};
